/**
 * @fileoverview Entidad User - Representa un usuario de la aplicación
 * Implementa patrón Value Object con inmutabilidad garantizada
 */

/**
 * @class User
 * @description Entidad principal que representa un usuario autenticado en el sistema
 * Implementación inmutable siguiendo principios de DDD (Domain-Driven Design)
 */
export default class User {
  /**
   * @constructor
   * @param {Object} props - Propiedades del usuario
   * @param {string} props.uid - ID único del usuario (normalmente de Firebase Auth)
   * @param {string} props.storeId - ID de la tienda en Tiendanube
   * @param {string} props.email - Email del usuario/tienda
   * @param {string} props.storeName - Nombre de la tienda
   * @param {string} props.role - Rol del usuario (admin, user, etc)
   * @param {Date} props.createdAt - Fecha de creación
   * @param {Object} props.metadata - Metadatos adicionales
   */
  constructor({
    uid,
    storeId,
    email = null,
    storeName = null,
    role = 'user',
    createdAt = new Date(),
    metadata = {}
  }) {
    this._validateProps({ uid, storeId, email, role });
    
    // Propiedades inmutables (Object.freeze en el constructor)
    this._uid = uid;
    this._storeId = storeId;
    this._email = email;
    this._storeName = storeName;
    this._role = role;
    this._createdAt = createdAt instanceof Date ? createdAt : new Date(createdAt);
    this._metadata = Object.freeze({ ...metadata });
    
    // Congelar la instancia para garantizar inmutabilidad
    Object.freeze(this);
  }
  
  /**
   * Validación de propiedades según reglas de negocio
   * @private
   * @param {Object} props - Propiedades a validar
   * @throws {Error} Si alguna validación falla
   */
  _validateProps({ uid, storeId, email, role }) {
    if (!uid || typeof uid !== 'string') {
      throw new Error('User must have a valid uid string');
    }
    
    if (!storeId || typeof storeId !== 'string') {
      throw new Error('User must have a valid storeId string');
    }
    
    if (email && typeof email !== 'string') {
      throw new Error('Email must be a string if provided');
    }
    
    if (!['admin', 'user', 'guest'].includes(role)) {
      throw new Error('Role must be one of: admin, user, guest');
    }
  }
  
  // Getters - No hay setters para garantizar inmutabilidad
  get uid() { return this._uid; }
  get storeId() { return this._storeId; }
  get email() { return this._email; }
  get storeName() { return this._storeName; }
  get role() { return this._role; }
  get createdAt() { return new Date(this._createdAt); } // Devuelve copia para evitar modificaciones
  get metadata() { return { ...this._metadata }; } // Devuelve copia para evitar modificaciones
  
  /**
   * Crea una nueva instancia con propiedades actualizadas (patrón inmutable)
   * @param {Object} props - Propiedades que se desean actualizar
   * @returns {User} Nueva instancia de User con propiedades actualizadas
   */
  with(props) {
    return new User({
      uid: this._uid,
      storeId: this._storeId,
      email: this._email,
      storeName: this._storeName,
      role: this._role,
      createdAt: this._createdAt,
      metadata: this._metadata,
      ...props
    });
  }
  
  /**
   * Verifica si el usuario tiene un rol específico o superior
   * @param {string} role - Rol a verificar
   * @returns {boolean} True si tiene el rol o superior
   */
  hasRole(role) {
    const roles = { 'guest': 1, 'user': 2, 'admin': 3 };
    return roles[this._role] >= roles[role];
  }
  
  /**
   * Convierte la entidad a un objeto plano para serialización
   * @returns {Object} Representación plana del usuario
   */
  toJSON() {
    return {
      uid: this._uid,
      storeId: this._storeId,
      email: this._email,
      storeName: this._storeName,
      role: this._role,
      createdAt: this._createdAt.toISOString(),
      metadata: this._metadata
    };
  }
}

// Exportado con ES Modules (export default) al inicio de la clase
